import React from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  IconButton,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const normalizePlaceholder = placeholder => {
  const name = String(placeholder?.name || '').trim();
  if (!name) return null;

  return {
    id: `${placeholder.placetype || 'other'}:${name}`,
    type: placeholder.placetype || 'other',
    name: placeholder.displayname || name,
    code: name.startsWith('{{') ? name : `{{${name}}}`,
  };
};

export default function EmailTemplatePlaceholder({
  value = '',
  onChange,
  textAreaRef = null,
  quillRef = null,
  insertAtCursor = false,
  placeholders = [],
  title = '',
  searchPlaceholder = '',
  groupNames = {},
  disabled = false,
  onInsert = null,
}) {
  const [anchorElement, setAnchorElement] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [expandedGroups, setExpandedGroups] = React.useState({});
  const cursorPosition = React.useRef(0);

  React.useEffect(() => {
    if (disabled) {
      setAnchorElement(null);
      setSearch('');
    }
  }, [disabled]);

  const groups = React.useMemo(() => {
    const normalized = placeholders.map(normalizePlaceholder).filter(Boolean);
    const availableGroups = [];
    const groupByType = new Map();

    normalized.forEach(placeholder => {
      if (!groupByType.has(placeholder.type)) {
        const group = {
          id: placeholder.type,
          name:
            groupNames[placeholder.type] ||
            groupNames[String(placeholder.type)] ||
            String(placeholder.type || 'Outros'),
          placeholders: [],
        };
        groupByType.set(placeholder.type, group);
        availableGroups.push(group);
      }
      groupByType.get(placeholder.type).placeholders.push(placeholder);
    });

    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return availableGroups;

    return availableGroups
      .map(group => ({
        ...group,
        placeholders: group.placeholders.filter(
          placeholder =>
            placeholder.name.toLowerCase().includes(normalizedSearch) ||
            placeholder.code.toLowerCase().includes(normalizedSearch)
        ),
      }))
      .filter(group => group.placeholders.length > 0);
  }, [groupNames, placeholders, search]);

  const captureCursor = () => {
    if (quillRef?.current) {
      const editor = quillRef.current.getEditor?.();
      const selection = editor?.getSelection();
      cursorPosition.current = selection?.index ?? editor?.getLength?.() ?? 0;
      return;
    }

    if (textAreaRef?.current) {
      cursorPosition.current = textAreaRef.current.selectionStart || 0;
    }
  };

  const open = event => {
    captureCursor();
    setSearch('');
    setAnchorElement(event.currentTarget);
  };

  const close = () => {
    setAnchorElement(null);
    setSearch('');
  };

  const toggleGroup = groupId => {
    setExpandedGroups(current => ({
      ...current,
      [groupId]: current[groupId] === undefined ? false : !current[groupId],
    }));
  };

  const selectPlaceholder = placeholder => {
    if (onInsert) {
      onInsert(placeholder.code);
      close();
      return;
    }

    if (quillRef?.current) {
      const editor = quillRef.current.getEditor?.();
      if (editor) {
        const index = cursorPosition.current;
        editor.insertText(index, placeholder.code, 'user');
        close();
        setTimeout(() => {
          editor.setSelection(index + placeholder.code.length, 0, 'user');
          editor.focus();
        }, 50);
        return;
      }
    }

    const currentValue = value || '';
    const index =
      insertAtCursor && textAreaRef?.current
        ? cursorPosition.current
        : currentValue.length;
    const nextValue =
      currentValue.slice(0, index) +
      placeholder.code +
      currentValue.slice(index);
    const scrollTop = textAreaRef?.current?.scrollTop;

    onChange(nextValue);
    close();

    if (insertAtCursor && textAreaRef?.current) {
      setTimeout(() => {
        const nextCursorPosition = index + placeholder.code.length;
        textAreaRef.current.focus();
        textAreaRef.current.setSelectionRange(
          nextCursorPosition,
          nextCursorPosition
        );
        if (scrollTop !== undefined) textAreaRef.current.scrollTop = scrollTop;
      }, 50);
    }
  };

  return (
    <>
      <Button
        aria-label={title}
        color="success"
        disabled={disabled}
        onClick={open}
        sx={{ minWidth: 40, width: 40, height: 40, p: 0 }}
        type="button"
        variant="contained"
      >
        <AddIcon />
      </Button>

      <Popover
        anchorEl={anchorElement}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={close}
        open={Boolean(anchorElement)}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ width: 380, maxWidth: 'calc(100vw - 32px)' }}>
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'grey.100',
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
            }}
          >
            <Typography fontWeight={600}>{title}</Typography>
            <IconButton aria-label="close" onClick={close} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 1.5 }}>
            <TextField
              autoFocus
              fullWidth
              onChange={event => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              size="small"
              value={search}
            />
          </Box>

          <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {groups.map(group => {
              const expanded = expandedGroups[group.id] !== false;
              return (
                <Box key={group.id}>
                  <ButtonBase
                    onClick={() => toggleGroup(group.id)}
                    sx={{
                      alignItems: 'center',
                      bgcolor: 'grey.100',
                      borderBottom: 1,
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.25,
                      width: '100%',
                    }}
                  >
                    <Typography fontSize={14} fontWeight={500}>
                      {group.name}
                    </Typography>
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 150ms',
                      }}
                    />
                  </ButtonBase>
                  <Collapse in={expanded}>
                    {group.placeholders.map(placeholder => (
                      <ButtonBase
                        key={placeholder.id}
                        onClick={() => selectPlaceholder(placeholder)}
                        sx={{
                          alignItems: 'flex-start',
                          borderBottom: 1,
                          borderColor: 'grey.100',
                          display: 'flex',
                          flexDirection: 'column',
                          px: 3,
                          py: 1.25,
                          textAlign: 'left',
                          width: '100%',
                        }}
                      >
                        <Typography fontSize={13}>{placeholder.name}</Typography>
                        <Typography
                          component="span"
                          sx={{
                            bgcolor: 'rgba(25, 118, 210, 0.08)',
                            borderRadius: 0.5,
                            color: 'primary.main',
                            fontFamily: 'monospace',
                            fontSize: 12,
                            mt: 0.25,
                            px: 0.75,
                            py: 0.25,
                          }}
                        >
                          {placeholder.code}
                        </Typography>
                      </ButtonBase>
                    ))}
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </>
  );
}
